/**
 * RitualMediaCarousel Component
 * Media carousel with navigation buttons and dot indicators.
 * Supports images, audio, native Cloudflare-hosted video (issue #362),
 * and legacy YouTube/Vimeo embeds.
 */

import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import { Pause, Play } from "lucide-react"
import * as React from "react"
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { RitualVideo } from "../../services/ritual.service"
import type { RitualMedia } from "../../types/detail.types"
import { RitualVideoPlayer } from "../RitualVideoPlayer"
import { RitualAudioPlayer } from "./RitualAudioPlayer"

interface RitualMediaCarouselProps {
  media: RitualMedia[]
  ritualId?: string
  requiresSignedToken?: boolean
  className?: string
  isGuest?: boolean
  /** When true, the authenticated user has permission to play media (joined/owner/organizer). */
  canAccessContent?: boolean
  onGuestMediaPlay?: () => void
}

/** Adapt a RitualMedia item carrying Cloudflare fields back to the
 *  RitualVideo shape the dedicated player consumes. */
const mediaItemToRitualVideo = (item: RitualMedia, ritualId: string): RitualVideo => ({
  id: item.id,
  ritualId,
  videoUrl: null,
  cloudflareUid: item.cloudflareUid ?? null,
  status: item.cloudflareStatus ?? "ready",
  duration: item.cloudflareDuration ?? null,
  failureReason: item.cloudflareFailureReason ?? null,
  thumbnailUrl: item.thumbnailUrl ?? null,
  title: item.title ?? null,
  sortOrder: 0,
  createdAt: "",
  updatedAt: "",
})

export function RitualMediaCarousel({
  media,
  ritualId,
  requiresSignedToken = false,
  className: _className,
  isGuest = false,
  canAccessContent = true,
  onGuestMediaPlay,
}: RitualMediaCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>()
  const { t } = useTranslation("rituals")
  const [current, setCurrent] = React.useState(0)
  const [count, setCount] = React.useState(0)
  // Which legacy YouTube/Vimeo slide is currently playing (controls the
  // iframe vs thumbnail+play-button swap). Cloudflare videos manage their
  // own playback inside RitualVideoPlayer, so they don't use this.
  const [playingIndex, setPlayingIndex] = React.useState<number | null>(null)
  // Whether a Cloudflare video is currently playing
  const [isCloudflareVideoPlaying, setIsCloudflareVideoPlaying] = React.useState(false)

  const isVideoPlaying = playingIndex !== null || isCloudflareVideoPlaying

  React.useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
      // Stop any playing legacy embed when navigating away from its slide.
      setPlayingIndex(null)
    })
  }, [api])

  const placeholderCount = media.length > 0 ? media.length : 4

  // Pick the cover image to render behind audio slides. Prefers an explicit
  // isCover image, falls back to the first image in the list.
  const audioCoverImage = React.useMemo(() => {
    const explicitCover = media.find(m => m.type === "image" && m.isCover)
    if (explicitCover) return explicitCover.url
    const firstImage = media.find(m => m.type === "image")
    return firstImage?.url
  }, [media])

  const handlePlayVideo = (index: number) => setPlayingIndex(index)
  const handlePauseVideo = () => setPlayingIndex(null)

  return (
    <div className="w-full relative">
      <Carousel
        setApi={setApi}
        plugins={media.length > 1 ? [WheelGesturesPlugin()] : []}
        opts={{ watchDrag: media.length > 1 }}
        className="w-full"
      >
        {media.length > 1 && (
          <>
            <CarouselPrevious className="absolute left-5 md:left-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 hover:border-white/20 bg-white/5 backdrop-blur-xs border-gradient-glass hover:border-gradient-glass" />
            <CarouselNext className="absolute right-5 md:right-18 z-20 md:h-10 md:w-10 lg:h-15 lg:w-15 border-1 border-white/20 hover:border-white/20 bg-white/5 backdrop-blur-xs border-gradient-glass hover:border-gradient-glass" />
          </>
        )}
        <CarouselContent>
          {media.length > 0
            ? media.map((item, index) => (
                <CarouselItem key={item.id}>
                  <div className="max-h-[600px] w-full overflow-hidden relative min-[1445px]:rounded-[30px]">
                    {item.type === "image" ? (
                      <img
                        src={item.url}
                        alt={`Ritual media ${index + 1}`}
                        className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                      />
                    ) : item.type === "audio" ? (
                      <RitualAudioPlayer
                        audioUrl={item.url}
                        coverImageUrl={item.thumbnailUrl || audioCoverImage}
                        onGuestPlay={isGuest || !canAccessContent ? onGuestMediaPlay : undefined}
                        active={index === current}
                      />
                    ) : (isGuest || !canAccessContent) && (item.cloudflareUid || item.url) ? (
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px]">
                        <img
                          src={item.thumbnailUrl || audioCoverImage || undefined}
                          alt={item.title || "Video thumbnail"}
                          className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                        />
                        <button
                          onClick={() => onGuestMediaPlay?.()}
                          className="absolute inset-0 flex items-center justify-center"
                          aria-label={t("mediaCarousel.play")}
                        >
                          <div className="p-4 bg-black/50 rounded-full">
                            <Play className="h-10 w-10 text-white" fill="white" />
                          </div>
                        </button>
                      </div>
                    ) : item.cloudflareUid && ritualId ? (
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px] overflow-hidden">
                        <RitualVideoPlayer
                          ritualId={ritualId}
                          video={mediaItemToRitualVideo(item, ritualId)}
                          requiresSignedToken={requiresSignedToken}
                          className="min-[1445px]:rounded-[30px]"
                          onPlay={() => setIsCloudflareVideoPlaying(true)}
                          onPause={() => setIsCloudflareVideoPlaying(false)}
                          onEnded={() => setIsCloudflareVideoPlaying(false)}
                          active={index === current}
                        />
                      </div>
                    ) : item.url ? (
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px]">
                        {playingIndex === index ? (
                          <iframe
                            src={`${item.url}&autoplay=1`}
                            className="absolute inset-0 w-full h-full min-[1445px]:rounded-[30px]"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                          />
                        ) : (
                          <>
                            <img
                              src={item.thumbnailUrl || undefined}
                              alt={item.title || "Video thumbnail"}
                              className="max-h-[600px] w-full object-cover min-[1445px]:rounded-[30px]"
                            />
                            <button
                              onClick={() => handlePlayVideo(index)}
                              className="absolute inset-0 flex items-center justify-center"
                              aria-label={t("mediaCarousel.play")}
                            >
                              <div className="p-4 bg-black/50 rounded-full">
                                <Play className="h-10 w-10 text-white" fill="white" />
                              </div>
                            </button>
                          </>
                        )}
                        {/* Pause button when playing */}
                        {playingIndex === index && (
                          <button
                            onClick={handlePauseVideo}
                            className="absolute inset-0 flex items-center justify-center"
                            aria-label={t("mediaCarousel.pause")}
                          >
                            <Pause className="h-8 w-8 text-white" fill="white" />
                          </button>
                        )}
                      </div>
                    ) : (
                      // No playable source -- shouldn't happen post-service
                      // filtering, but render an empty slide instead of an
                      // iframe with src="&autoplay=1" (the browser would
                      // resolve that as a relative URL inside the React app
                      // and start a recursive ritual-detail fetch loop).
                      <div className="relative w-full h-[240px] md:h-[600px] min-[1445px]:rounded-[30px] bg-card" />
                    )}
                  </div>
                </CarouselItem>
              ))
            : Array.from({ length: placeholderCount }).map((_, index) => (
                <CarouselItem key={index}>
                  <div className="max-h-[600px] w-full overflow-hidden bg-card" />
                </CarouselItem>
              ))}
        </CarouselContent>
      </Carousel>

      {/* Dot Indicators - only show when multiple slides and no video is playing */}
      {media.length > 1 && !isVideoPlaying && (
        <div className="mb-[30px] flex items-center justify-center gap-2 absolute inset-x-0 bottom-0">
          {Array.from({ length: count || placeholderCount }).map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1 w-8 rounded-none transition-all",
                index === current ? "bg-brand-offwhite" : "bg-brand-offwhite opacity-20",
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
